/**
 * 应用壳：hash 路由 + 底部四 Tab 导航（排盘 / 学习 / AI 对话 / 我的）
 * - #/paipan 排盘主界面（命盘 + 大限栏；#/chart 兼容旧链接）
 * - #/cases 命例管理页；#/case-form 命例表单（?edit=<chartId> 修改）—— 归属「排盘」Tab
 * - #/learn 学习；#/chat AI 对话（占位）；#/mine 我的
 * 启动逻辑：有命例 → 恢复最近一次打开的命盘；无命例 → 进入命例列表页
 * 顶栏吸顶常驻；左侧仅放有导航功能的元素（排盘域「命例」、学习详情「← 返回」），
 * Tab 页不重复显示标题（位置指示由底部 TabBar 承担）；-mx 抵消按钮 padding 使文字贴左缘对齐
 */
import { useEffect, useState } from 'react';
import ChartPage from './pages/ChartPage';
import CasePage from './pages/CasePage';
import CaseFormPage from './pages/CaseFormPage';
import LearnPage from './pages/LearnPage';
import ChatPage from './pages/ChatPage';
import MinePage from './pages/MinePage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MembershipPage from './pages/MembershipPage';
import AppDownloadPage from './pages/AppDownloadPage';
import TabBar, { tabOfPage } from './components/TabBar';
import TtsNowPlayingBar from './components/TtsNowPlayingBar';
import { useChartStore } from './state/store';
import { useLearnStore } from './state/learn-store';
import { useMembershipStore } from './state/membership-store';
import { setScreenProtected, isNativeApp } from '@shared/api/screenGuard';

export type Page = 'paipan' | 'cases' | 'case-form' | 'learn' | 'chat' | 'mine' | 'register' | 'reset' | 'membership' | 'download';

// App 独占页（web 端不可达，命中回落到下载引导页）
// 注：web 保留匿名排盘 + 注册/登录/命例云同步（mine/register），故仅锁学习/AI对话/会员/找回密码
const APP_ONLY: Page[] = ['learn', 'chat', 'membership', 'reset'];
const IS_APP = isNativeApp();

function readHash(): Page {
  const h = window.location.hash;
  if (h.startsWith('#/case-form')) return 'case-form';
  if (h.startsWith('#/cases')) return 'cases';
  if (h.startsWith('#/learn')) return 'learn';
  if (h.startsWith('#/chat')) return 'chat';
  if (h.startsWith('#/membership')) return 'membership';
  if (h.startsWith('#/register')) return 'register';
  if (h.startsWith('#/reset')) return 'reset';
  if (h.startsWith('#/mine')) return 'mine';
  if (h.startsWith('#/download')) return 'download';
  return 'paipan'; // 含 #/chart 兼容
}

export default function App() {
  const [page, setPage] = useState<Page>(readHash);
  const [booted, setBooted] = useState(false);
  // web 端命中 App 独占页 → 有效页回落到下载引导（配合下方 effect 改 hash）
  const eff: Page = !IS_APP && APP_ONLY.includes(page) ? 'download' : page;
  const tab = tabOfPage(eff);
  const inPaipan = tab === 'paipan';
  // 学习页详情态：顶栏左上角「学习」→「← 返回」（详情已入 hash 子路由，返回走 history.back）
  const learnSelected = useLearnStore((s) => s.selectedId);
  // 会员现状（阶段二）：token 变化刷 me；白名单决定原生截屏保护
  const token = useChartStore((s) => s.token);
  const allowCapture = useMembershipStore((s) => s.me?.allow_capture ?? false);

  useEffect(() => {
    void useMembershipStore.getState().refresh(token);
  }, [token]);

  // FLAG_SECURE：非白名单用户全程禁截屏/录屏（白名单放行后允许学习页复制截图）；
  // 未登录/me 未返回时 allowCapture=false，默认保护
  useEffect(() => {
    void setScreenProtected(!allowCapture);
  }, [allowCapture]);

  useEffect(() => {
    const onHash = () => setPage(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // web 端误入 App 独占页 → 跳下载引导
  useEffect(() => {
    if (!IS_APP && APP_ONLY.includes(page)) window.location.hash = '#/download';
  }, [page]);

  // 回到前台 / 窗口聚焦：拉一次云端（App 切后台再回来、Web 切标签页回来时同步；syncCloud 内部有并发守卫）
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void useChartStore.getState().syncCloud();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // 启动：恢复命例数据；有最近命例则重建盘，无命例则进入命例列表页
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const st = useChartStore.getState();
      await st.loadCases();
      if (cancelled) return;
      const { lastChartId, cases } = useChartStore.getState();
      const last = cases.find((c) => c.chartId === lastChartId) ?? cases[0];
      if (last) {
        await useChartStore.getState().createChart(last.input);
      } else if (!cancelled && readHash() === 'paipan') {
        window.location.hash = '#/cases';
      }
      if (!cancelled) setBooted(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="app-header sticky top-0 z-30 bg-slate-200 pb-3 text-slate-900">
        {/* h-8 固定行盒：四个页面顶栏等高（最高分支「← 返回」≈30px < 32px），
            各标题/链接字号与 padding 差异由 items-center 居中吸收，切页不再顶栏跳高 */}
        <div className="mx-auto flex h-8 max-w-6xl items-center justify-between px-4">
          <div className="flex items-center gap-2">
            {inPaipan ? (
              <a
                href="#/cases"
                className={`-mx-3 rounded px-3 py-1 text-sm font-bold ${eff === 'cases' || eff === 'case-form' ? 'bg-slate-500 text-white' : 'hover:bg-slate-300'}`}
              >
                命例
              </a>
            ) : tab === 'learn' && learnSelected != null ? (
              <button
                type="button"
                onClick={() => window.history.back()}
                className="-mx-2 rounded px-2 py-1 text-base font-semibold hover:bg-slate-300"
              >
                ← 返回
              </button>
            ) : eff === 'membership' ? (
              <a
                href="#/mine"
                className="-mx-2 rounded px-2 py-1 text-base font-semibold hover:bg-slate-300"
              >
                ← 返回
              </a>
            ) : null}
          </div>
        </div>
      </header>
      <main className="app-main mx-auto max-w-6xl px-4 pt-5">
        {!booted ? (
          <div className="py-20 text-center text-sm text-slate-400">加载中…</div>
        ) : eff === 'paipan' ? (
          <ChartPage />
        ) : eff === 'cases' ? (
          <CasePage />
        ) : eff === 'case-form' ? (
          <CaseFormPage />
        ) : eff === 'learn' ? (
          <LearnPage />
        ) : eff === 'chat' ? (
          <ChatPage />
        ) : eff === 'mine' ? (
          <MinePage />
        ) : eff === 'register' ? (
          <RegisterPage />
        ) : eff === 'reset' ? (
          <ResetPasswordPage />
        ) : eff === 'membership' ? (
          <MembershipPage />
        ) : eff === 'download' ? (
          <AppDownloadPage />
        ) : (
          <ChartPage />
        )}
      </main>
      <TabBar page={eff} isApp={IS_APP} />
      {/* 「正在播放」浮层（App 独占、仅学习 Tab）：锁屏/切后台回学习页时快速定位正在播章节 */}
      <TtsNowPlayingBar inLearnTab={tab === 'learn'} />
    </div>
  );
}
